"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";

type Phase = "idle" | "loading" | "finishing";

function isModifiedEvent(e: MouseEvent) {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
}

function findAnchor(el: EventTarget | null): HTMLAnchorElement | null {
  let node = el as HTMLElement | null;
  while (node) {
    if (node.tagName === "A") return node as HTMLAnchorElement;
    node = node.parentElement;
  }
  return null;
}

export default function RouteProgressBar() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchString = useMemo(
    () => searchParams?.toString() ?? "",
    [searchParams]
  );

  const [mounted, setMounted] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [pct, setPct] = useState(0);

  const startDelayT = useRef<number | null>(null);
  const creepT = useRef<number | null>(null);
  const finishT = useRef<number | null>(null);
  const resetT = useRef<number | null>(null);
  const maxT = useRef<number | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const clearTimers = useCallback(() => {
    if (startDelayT.current) window.clearTimeout(startDelayT.current);
    if (creepT.current) window.clearTimeout(creepT.current);
    if (finishT.current) window.clearTimeout(finishT.current);
    if (resetT.current) window.clearTimeout(resetT.current);
    if (maxT.current) window.clearTimeout(maxT.current);

    startDelayT.current =
      creepT.current =
      finishT.current =
      resetT.current =
      maxT.current =
        null;
  }, []);

  const startCreep = useCallback(() => {
    const tick = () => {
      setPct((p) => {
        if (p >= 95) return p;
        const step = p < 90 ? 0 : p < 93 ? 0.6 : 0.25;
        return Math.min(95, p + step);
      });
      creepT.current = window.setTimeout(tick, 120);
    };
    creepT.current = window.setTimeout(tick, 120);
  }, []);

  const finish = useCallback(() => {
    clearTimers();
    setPhase((prev) => (prev === "idle" ? "idle" : "finishing"));
    setPct(100);

    finishT.current = window.setTimeout(() => {
      setPhase("idle");
      resetT.current = window.setTimeout(() => setPct(0), 0);
    }, 180);
  }, [clearTimers]);

  const start = useCallback(() => {
    clearTimers();

    maxT.current = window.setTimeout(() => {
      finish();
    }, 2500);

    startDelayT.current = window.setTimeout(() => {
      setPhase("loading");
      setPct(15);
      requestAnimationFrame(() => setPct(90));
      startCreep();
    }, 80);
  }, [clearTimers, startCreep, finish]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (isModifiedEvent(e)) return;

      const a = findAnchor(e.target);
      if (!a) return;

      const hrefAttr = a.getAttribute("href") || "";
      if (!hrefAttr) return;
      if (hrefAttr.startsWith("#")) return;

      if (a.target && a.target !== "_self") return;
      if (a.hasAttribute("download")) return;

      let url: URL;
      try {
        url = new URL(hrefAttr, window.location.href);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) return;

      const current = new URL(window.location.href);
      const samePathAndSearch =
        url.pathname === current.pathname && url.search === current.search;

      if (samePathAndSearch) return;

      start();
    };

    const onPopState = () => start();

    document.addEventListener("click", onClick, true);
    window.addEventListener("popstate", onPopState);

    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("popstate", onPopState);
      clearTimers();
    };
  }, [start, clearTimers]);

  useEffect(() => {
    finish();
  }, [pathname, searchString, finish]);

  if (!mounted) return null;

  const barClasses = [
    "fixed left-0 top-0 z-[9999] h-[3px]",
    "bg-fuji-green",
    phase === "idle" ? "hidden" : "block",
    phase === "idle"
      ? "transition-none"
      : "transition-[width] duration-200 ease-out",
  ].join(" ");

  return (
    <div
      aria-hidden="true"
      className={barClasses}
      style={{ width: `${pct}%` }}
    />
  );
}